import { Bot, Rocket, Settings2, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AssistantStatusToggle } from '@/components/assistants';
import { PromptPersonalizationTab } from '@/components/prompt-editor';
import {
  useMyIABlueAssistant,
  useUpdateMyIABlueAssistant,
  useRegenerateMyIABlueWebhook,
} from '@/hooks/useMyIABlueAssistant';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/error-handler';
import type { UpdateAssistantDto } from '@/types';
import { DeploymentForm } from '@/components/assistants/DeploymentForm';
import { BehaviorForm } from '@/components/assistants/BehaviorForm';

export function AISettingsPage() {
  const { toast } = useToast();

  // Queries & Mutations
  const { data: assistant, isLoading, error } = useMyIABlueAssistant();
  const updateMutation = useUpdateMyIABlueAssistant();
  const regenerateWebhookMutation = useRegenerateMyIABlueWebhook();

  // Handlers
  const handleFormSubmit = async (data: UpdateAssistantDto) => {
    try {
      await updateMutation.mutateAsync(data);
      toast({
        title: 'Sucesso',
        description: 'Configurações salvas com sucesso',
      });
    } catch (error: unknown) {
      const message = getErrorMessage(error, 'Erro ao salvar configurações');
      toast({
        title: 'Erro',
        description: message,
        variant: 'destructive',
      });
    }
  };

  const handleRegenerateWebhook = async () => {
    try {
      const result = await regenerateWebhookMutation.mutateAsync();
      toast({
        title: 'Sucesso',
        description: 'Webhook regenerado com sucesso',
      });

      // Auto-copy new webhook URL
      await navigator.clipboard.writeText(result.webhookUrl);
      toast({
        title: 'Copiado',
        description: 'Novo webhook URL copiado para a área de transferência',
      });
    } catch (error: unknown) {
      const message = getErrorMessage(error, 'Erro ao regenerar webhook');
      toast({
        title: 'Erro',
        description: message,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="container max-w-4xl py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2 mb-2">
          <Bot className="h-8 w-8" />
          My IABlue
        </h1>
        <p className="text-muted-foreground">
          Configure seu assistente de IA para automatizar atendimentos
        </p>
      </div>

      {/* Content */}
      {isLoading ? (
        <Card className="animate-pulse">
          <CardHeader>
            <div className="h-6 bg-muted rounded w-1/3 mb-2"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="h-10 bg-muted rounded"></div>
            <div className="h-10 bg-muted rounded"></div>
            <div className="h-10 bg-muted rounded"></div>
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="border-destructive">
          <CardHeader className="text-center py-12">
            <Bot className="h-16 w-16 mx-auto text-destructive mb-4" />
            <CardTitle className="text-destructive">Erro ao carregar configurações</CardTitle>
            <CardDescription>
              {(error as Error)?.message || 'Não foi possível carregar as configurações do assistente'}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : assistant ? (
        <div className="space-y-6">
          {/* Status Toggle */}
          <AssistantStatusToggle currentStatus={assistant.status} />

          {/* Tabs */}
          <Tabs defaultValue="deployment" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="deployment" className="flex items-center gap-1.5">
                <Rocket className="h-4 w-4" />
                <span className="hidden sm:inline">Implantação</span>
              </TabsTrigger>
              <TabsTrigger value="behavior" className="flex items-center gap-1.5">
                <Settings2 className="h-4 w-4" />
                <span className="hidden sm:inline">Comportamento</span>
              </TabsTrigger>
              <TabsTrigger value="personalization" className="flex items-center gap-1.5">
                <Sparkles className="h-4 w-4" />
                <span className="hidden sm:inline">Personalização</span>
              </TabsTrigger>
            </TabsList>

            {/* Tab: Deployment */}
            <TabsContent value="deployment">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Configurações de Implantação</CardTitle>
                  <CardDescription>
                    Informações básicas, canal e webhook do assistente
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <DeploymentForm
                    assistant={assistant}
                    onSubmit={handleFormSubmit}
                    onRegenerateWebhook={handleRegenerateWebhook}
                    isSubmitting={updateMutation.isPending}
                    isRegenerating={regenerateWebhookMutation.isPending}
                    error={updateMutation.error?.message}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab: Behavior */}
            <TabsContent value="behavior">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Configurações de Comportamento</CardTitle>
                  <CardDescription>
                    Buffer de mensagens, divisão de respostas e modo de teste
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <BehaviorForm
                    assistant={assistant}
                    onSubmit={handleFormSubmit}
                    isSubmitting={updateMutation.isPending}
                    error={updateMutation.error?.message}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab: Personalization */}
            <TabsContent value="personalization">
              <PromptPersonalizationTab />
            </TabsContent>
          </Tabs>
        </div>
      ) : null}
    </div>
  );
}
